import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowRight, Check, Clock, DollarSign, Flame, LockKeyhole, ShieldCheck, CalendarDays } from "lucide-react";
import { Reveal, MaskedLine } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";
import { STAGES, getStageStatus, DPS_MEDIA } from "../lib/content";
import { trackEvent } from "../lib/analytics";

const CLASSES = [
  {
    meta: "90 MIN · $20 · FRI 6:00 PM & SAT 11:30 AM",
    title: "HYROX Specialty Training Classes",
    body: "Coached specialty sessions offered Friday and Saturday that follow the 12-week periodized Hyrox Dallas program (Aug 28 – Nov 13). Each 1.5-hour class targets the current training stage — engine work, station strength and compromised running. Specialty classes are separate, paid sessions.",
  },
  {
    meta: "$25 · DOUBLES WAVES FIRST",
    title: "Full Simulation — Doubles",
    body: "An open course that includes all 8 Hyrox exercises and 1km runs, executed in order — raced you-go-I-go with a partner. At sign-up you'll rate yourself on a scale of 1–3 so waves are seeded fairly. Times may change slightly; final confirmation email goes out the morning before.",
  },
  {
    meta: "$25 · SINGLES WAVES AFTER DOUBLES",
    title: "Full Simulation — Singles",
    body: "The same open course raced solo: all 8 stations and every 1km run in official order, held to movement standards and timed. Rate yourself 1–3 at sign-up for wave seeding; final wave confirmation is emailed the morning before the simulation.",
  },
  {
    meta: "SEASONAL · FULL SIMULATION",
    title: "Season Simulations",
    body: "Full race simulations run through the season as the dress rehearsal before race weekend. Singles and doubles waves — open registrations appear on the Events page when dates are announced.",
  },
];
const STAGE_STATUS = getStageStatus();
const BOOKING_POLICIES = [
  "Bookings open up to 28 days before the class date.",
  "Specialty classes are $20, or four classes for $60 with a HYROX 4 Pack.",
  "Registration closes two hours before the class starts.",
  "Cancellations must be made more than four hours in advance.",
  "No-shows are subject to a $20 fee.",
];
const INFO = [
  { icon: Clock, k: "Sessions", v: "Fri 6:00 PM · Sat 11:30 AM · 90 minutes" },
  { icon: DollarSign, k: "Drop-In", v: "$20 per specialty session" },
  { icon: Flame, k: "Current Stage", v: "BASE — Aug 28 to Sept 19 · aerobic engine building" },
];

export default function Schedule() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash === "#book-now") {
      const t = setTimeout(() => {
        document.getElementById("book-now")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 350);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [location]);
  return (
    <div data-testid="schedule-page" className="pt-16">
      <Seo
        title="HYROX Class Schedule & Booking — Dallas | HyroxNinja"
        description="The Hyrox Ninja weekly schedule in Dallas: specialty classes Fridays 6:00 PM and Saturdays 11:30 AM, 90 minutes, $20. Full race simulations ($25, singles and doubles waves). Reserve on the Events page."
      />
      <section className="relative overflow-hidden">
        <img src={DPS_MEDIA.bg.schedule} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070707]/70 via-[#070707]/40 to-[#070707]" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-10">
        <p className="eyebrow mb-4">The Weekly Standard</p>
        <h1 className="font-head font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl mb-6">
          <MaskedLine delay={0.1}>Schedule &</MaskedLine>
          <MaskedLine delay={0.25} className="text-volt">Book Your Spot</MaskedLine>
        </h1>
        <p className="text-zinc-400 max-w-2xl leading-relaxed">Read the class formats, program and policies here — then reserve your spot on the Events page. Registration and payment happen natively on HyroxNinja with secure Stripe checkout.</p>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-6">
        <div className="grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-8">
          {INFO.map((i, idx) => (
            <Reveal key={i.k} delay={idx * 0.05} className="bg-[#0c0c0c] p-6 h-full">
              <div data-testid={`schedule-info-${idx}`}>
                <i.icon size={20} className="text-volt mb-3" />
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">{i.k}</p>
                <p className="font-head font-bold uppercase text-lg leading-snug">{i.v}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16" data-testid="booking-guide-section">
        <Reveal>
          <p className="eyebrow mb-3">Start Here</p>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-9">
            <div>
              <h2 className="font-head font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl mb-3">
                Know what you are booking
              </h2>
              <p className="text-zinc-400 max-w-2xl leading-relaxed">
                Every important detail lives here on HyroxNinja. Read the class format, program,
                pricing and policies first — then head to the Events page to claim a spot in an
                open session.
              </p>
            </div>
            <Link to="/events" className="btn-volt shrink-0" data-testid="jump-to-booking" onClick={() => trackEvent("booking_click", { placement: "schedule_guide", service: "general" })}>
              View open registrations <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
        <div className="grid lg:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {CLASSES.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.05} className="h-full">
              <article className="bg-[#0c0c0c] p-6 sm:p-8 h-full" data-testid={`class-card-${i}`}>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-volt mb-2">{c.meta}</p>
                <h3 className="font-head font-bold uppercase text-xl mb-3">{c.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{c.body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16" data-testid="program-stages-section">
        <Reveal>
          <p className="eyebrow mb-3">The 12-Week Program</p>
          <h2 className="font-head font-extrabold uppercase tracking-tight text-2xl sm:text-3xl mb-3">
            Aug 28 to race week
          </h2>
          <p className="text-zinc-400 max-w-2xl leading-relaxed mb-9">
            In HYROX training, Base, Pace, Accelerate and Prime describe different training
            intensities and goals that prepare athletes for the demands of competition. Here is
            the complete program breakdown, rebuilt as readable and indexable website content.
          </p>
        </Reveal>
        <div className="program-timeline border border-white/10">
          {STAGES.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.04}>
              <article className={`program-phase ${STAGE_STATUS?.index === i ? "is-active-stage" : ""}`} data-testid={`stage-card-${s.n}`}>
                <div className="program-phase-number">{s.n}</div>
                <div>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-volt mb-1">{s.meta}</p>
                  <h3 className="font-head font-bold uppercase text-xl mb-2">
                    {s.name}
                    {STAGE_STATUS?.index === i && (
                      <span className={`stage-badge ${STAGE_STATUS.label === "current" ? "is-live" : ""}`} data-testid="stage-status-badge">
                        {STAGE_STATUS.label === "current" ? "Current Stage" : "Up Next"}
                      </span>
                    )}
                  </h3>
                  <dl className="program-phase-copy max-w-4xl">
                    <div>
                      <dt>Definition</dt>
                      <dd>{s.definition}</dd>
                    </div>
                    <div>
                      <dt>Application</dt>
                      <dd>{s.application}</dd>
                    </div>
                  </dl>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <div className="program-join-note">
            <strong>Join the program at any time.</strong>
            <span>Although this is a progressive 12-week build toward Dallas HYROX, athletes can begin training during any stage of the program.</span>
          </div>
        </Reveal>
      </section>
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16" data-testid="booking-policies-section">
        <Reveal>
          <article className="bg-[#0c0c0c] border border-white/10 p-7 sm:p-9">
            <p className="eyebrow mb-3">Before You Reserve</p>
            <h2 className="font-head font-extrabold uppercase text-3xl tracking-tight mb-6">Pricing & policies</h2>
            <ul className="native-detail-list">
              {BOOKING_POLICIES.map((policy) => <li key={policy}><Check size={15} /> <span>{policy}</span></li>)}
            </ul>
          </article>
        </Reveal>
      </section>
      <section id="book-now" className="max-w-7xl mx-auto px-5 lg:px-8 pb-24 scroll-mt-24">
        <Reveal>
          <div className="border border-volt/40 bg-volt/5 p-8 lg:p-10" data-testid="schedule-reserve-card">
            <p className="eyebrow mb-3">Reserve on HyroxNinja</p>
            <h2 className="font-head font-extrabold uppercase tracking-tight text-3xl sm:text-4xl mb-3">Lock in your spot</h2>
            <p className="text-zinc-400 max-w-2xl leading-relaxed mb-7">
              Open sessions, simulations and partner workouts are listed on the Events page with
              live spot counts. Registration — and payment for paid events — happens right here,
              with instant on-screen confirmation.
            </p>
            <div className="flex flex-wrap gap-4 mb-7">
              <Link to="/events" className="btn-volt" data-testid="schedule-book-events-button" onClick={() => trackEvent("booking_click", { placement: "schedule_reserve", service: "general" })}>
                <CalendarDays size={16} /> View Open Registrations <ArrowRight size={16} />
              </Link>
              <Link to="/simulation" className="btn-ghost" data-testid="schedule-sim-link">
                Simulation Details
              </Link>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-zinc-500 text-xs font-mono2 uppercase tracking-wider">
              <p className="flex items-center gap-2"><ShieldCheck size={14} /> Live spot counts</p>
              <p className="flex items-center gap-2"><LockKeyhole size={14} /> Secure Stripe checkout</p>
              <p className="flex items-center gap-2"><CalendarDays size={14} /> Instant confirmation</p>
            </div>
            <p className="text-zinc-500 text-xs leading-relaxed mt-6" data-testid="schedule-blog-links">
              First time? Read the booking guide to{" "}
              <Link to="/blog/hyrox-classes-near-me-dallas" className="text-volt hover:text-white transition-colors underline underline-offset-2">HYROX classes in Dallas</Link>
              {" "}and the breakdown of{" "}
              <Link to="/blog/hyrox-training-cost-dallas" className="text-volt hover:text-white transition-colors underline underline-offset-2">what HYROX training costs</Link>.
            </p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
